import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  DotNestedKeys,
  MenuOptionValue,
  NestedPick,
  Paginated,
  PaginationQuery,
  PaginationQueryWithFields,
  Version,
  Versions,
} from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import {
  AssignNewHighwayPass,
  HighwayPass,
  HighwayPassNoCarDependency,
  HighwayPassVersion,
  UpsertHighwayPass,
} from "./types";

export const useHighwayPassesApi = () => {
  const api = useApi();

  const getAdminHighwayPass = useCallback(
    (highwayPassId: string, signal: AbortSignal) => {
      const url = `/admin/highway_passes/${highwayPassId}`;
      return useSWRMutation<HighwayPass | null, CustomError, string>(url, (url) =>
        api.get<HighwayPass>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminHighwayPasses = useCallback(
    <F extends DotNestedKeys<HighwayPassNoCarDependency>>(
      query: PaginationQueryWithFields<HighwayPassNoCarDependency, F>,
      signal: AbortSignal,
      carId?: string,
    ) => {
      const url = buildUrl<NestedPick<HighwayPassNoCarDependency, F>>(
        carId ? `/admin/cars/${carId}/highway_passes` : "/admin/highway_passes",
        query,
      );
      return useSWR<Paginated<NestedPick<HighwayPassNoCarDependency, F>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<HighwayPassNoCarDependency, F>>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminHighwayPass = useCallback(
    async (body: UpsertHighwayPass): Promise<{ id: string }> => {
      const url = `/admin/highway_passes`;
      const id = await api.post<UpsertHighwayPass, { id: string }>(url, body);
      api.mutateCache([url, `/admin/highway_passes`, `/admin/cars/${body.carId}/highway_passes`]);
      return id;
    },
    [api],
  );

  const updateAdminHighwayPass = useCallback(
    async (id: string, body: UpsertHighwayPass) => {
      const url = `/admin/highway_passes/${id}`;
      await api.put<UpsertHighwayPass>(url, body);
      api.mutateCache([
        `/admin/highway_passes`,
        `/admin/cars/${body.carId}/highway_passes`,
        `/admin/highway_passes/${id}/versions`,
      ]);
    },
    [api],
  );

  const deleteAdminHighwayPass = useCallback(
    async (highwayPassesId: string, carId?: string): Promise<void> => {
      const url = `/admin/highway_passes/${highwayPassesId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/highway_passes`, `/admin/cars/${carId}/highway_passes`]);
      });
    },
    [api],
  );

  const assignNewHighwayPass = useCallback(
    async (body: AssignNewHighwayPass) => {
      const url = `/admin/cars/${body.carId}/assign_new_hp`;
      await api.post<AssignNewHighwayPass, { id: string }>(url, body);
      api.mutateCache([
        `/admin/cars`,
        `/admin/cars/${body.carId}/overview`,
        `/admin/cars/${body.carId}/highway_passes`,
      ]);
    },
    [api],
  );

  const getAdminHighwayPassHistory = useCallback(
    (query: PaginationQuery, highwayPassID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/highway_passes/${highwayPassID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminHighwayPassVersion = useCallback(
    (highwayPassId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/highway_passes/${highwayPassId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<HighwayPassVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<HighwayPassVersion>>({ url, signal }),
      );
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/highway_passes/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  return {
    updateAdminHighwayPass,
    deleteAdminHighwayPass,
    insertAdminHighwayPass,
    useAdminHighwayPasses,
    assignNewHighwayPass,
    getAdminHighwayPassHistory,
    getAdminHighwayPassVersion,
    getFieldFilters,
    getAdminHighwayPass,
  };
};
